import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { Notice } from '@/components/admin/Notice';
import { SkeletonRows } from '@/components/admin/states';
import { cancelSlot, createSlot, fetchCandidateSlots } from '@/features/availability/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime } from '@/lib/datetime';

/**
 * 事務局が面談の候補日を管理する（D-183）.
 *
 * 日時未定の面談（D-173）で、受講生に見せる候補日を確認・追加・取消できる。
 * - サクセス面談: 担当サクセスが「実施可能日」から自分で登録するのが基本。
 *   ここからは事務局が代理で追加できる
 * - 1on1: 理事・講師には入力作業をさせない運用のため、
 *   事務局が理事から日程を聞き、ここで候補日を登録する
 *
 * 候補は相手（担当者）本人の枠として登録されるため、**その担当者の他の面談にも
 * 同じ候補が共有され、どれかで選ばれると全面談から消える**。
 */
export function MeetingCandidateManager({
  eventId,
  counterpartUserId,
  counterpartName,
}: {
  eventId: string;
  counterpartUserId: string;
  counterpartName: string | null;
}): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['candidate-slots', eventId],
    queryFn: () => fetchCandidateSlots(eventId),
  });
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['candidate-slots', eventId] });
  }

  async function onCreate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createSlot({
        starts_at: datetimeLocalToIso(startsAt),
        ends_at: datetimeLocalToIso(endsAt),
        owner_user_id: counterpartUserId,
      });
      await refresh();
      setStartsAt('');
      setEndsAt('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(slotId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await cancelSlot(slotId);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '取消に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-sm border border-wpborder bg-white px-4 py-3">
      <h2 className="mb-1 font-semibold text-wptext-strong">
        候補日{counterpartName ? `（${counterpartName} さんの実施可能日）` : ''}
      </h2>
      <p className="mb-3 text-wptext-muted">
        受講生はこの候補日から1つ選んで日程を確定します。担当者の他の面談にも同じ候補が
        共有され、どれかの面談で選ばれると残りの面談からは消えます。
      </p>
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {query.isPending ? (
        <SkeletonRows rows={2} columns={2} />
      ) : query.isError ? (
        <p className="text-wpnotice-error">候補日を読み込めませんでした。</p>
      ) : query.data.length === 0 ? (
        <p className="mb-2 text-wptext-muted">
          候補日はまだありません。下から追加するか、担当者に「実施可能日」の登録を依頼してください。
        </p>
      ) : (
        <ul className="mb-2 divide-y divide-wpborder">
          {query.data.map((slot) => (
            <li key={slot.id} className="flex flex-wrap items-center gap-3 py-1.5">
              <span className="tabular-nums">
                {formatDateTime(slot.starts_at)} 〜{' '}
                {new Date(slot.ends_at).toLocaleTimeString('ja-JP', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => void onCancel(slot.id)}
                className="text-wpnotice-error hover:underline disabled:opacity-50"
              >
                取り消す
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">開始</span>
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">終了</span>
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <button
          type="button"
          disabled={busy || !startsAt || !endsAt}
          onClick={() => void onCreate()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          候補日を追加する
        </button>
      </div>
    </section>
  );
}
